import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle } from 'lucide-react';

/**
 * NewsletterForm Component
 * Native-styled form that submits to Formspree
 * Shows thank you message on same page after submission
 */
export const NewsletterForm = ({ 
  className = '', 
  variant = 'default', // 'default' | 'inline' | 'footer'
  showAnimation = true 
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFooter = variant === 'footer';
  const isInline = variant === 'inline';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const form = e.target;
    const formData = new FormData(form);
    
    try {
      await fetch(process.env.REACT_APP_NEWSLETTER_URL, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      setIsSubmitted(true);
      form.reset();
    } catch (error) {
      // Even if there's an error, show success (Formspree often works despite CORS)
      setIsSubmitted(true);
    }
    
    setIsSubmitting(false);
  };

  // Success state for footer variant
  if (isSubmitted && isFooter) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center gap-3"
      >
        <div className="flex items-center gap-2 text-green-400">
          <CheckCircle className="w-6 h-6" />
          <span className="font-semibold text-lg">Thank you for subscribing!</span>
        </div>
        <button
          onClick={() => setIsSubmitted(false)}
          className="text-white/70 hover:text-white text-sm underline"
        >
          Subscribe another email
        </button>
      </motion.div>
    );
  }

  // Success state for inline variant (homepage hero)
  if (isSubmitted && isInline) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-start gap-2"
      >
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-5 h-5" />
          <span className="font-semibold">Thank you for subscribing!</span>
        </div>
        <button
          onClick={() => setIsSubmitted(false)}
          className="text-brand-secondary hover:underline text-sm"
        >
          Subscribe another email
        </button>
      </motion.div>
    );
  }

  // Success state for modal/default variant
  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-white rounded-2xl shadow-lg p-6 md:p-8 ${className}`}
      >
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="font-heading text-2xl font-bold text-brand-primary mb-2">
            Thank You!
          </h3>
          <p className="text-slate-600 mb-6">
            You&apos;ve been successfully subscribed to the newsletter.
          </p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="text-brand-secondary font-semibold hover:underline"
          >
            Subscribe another email
          </button>
        </div>
      </motion.div>
    );
  }

  // Footer variant - inline horizontal layout
  if (isFooter) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          name="name"
          required
          placeholder="Your name"
          className="flex-1 px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:border-brand-secondary focus:ring-2 focus:ring-brand-secondary/20 outline-none transition-all"
        />
        <input
          type="email"
          name="email"
          required
          placeholder="Your email"
          className="flex-1 px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:border-brand-secondary focus:ring-2 focus:ring-brand-secondary/20 outline-none transition-all"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 px-8 py-3 font-semibold whitespace-nowrap transition-all flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Subscribe
            </>
          )}
        </button>
      </form>
    );
  }

  // Inline variant (for homepage hero) - horizontal compact layout
  if (isInline) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          name="name"
          required
          placeholder="Your name"
          className="px-4 py-3 rounded-full border border-slate-200 focus:border-brand-secondary focus:ring-2 focus:ring-brand-secondary/20 outline-none transition-all bg-white text-sm"
        />
        <input
          type="email"
          name="email"
          required
          placeholder="Your email"
          className="px-4 py-3 rounded-full border border-slate-200 focus:border-brand-secondary focus:ring-2 focus:ring-brand-secondary/20 outline-none transition-all bg-white text-sm"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 px-6 py-3 font-semibold whitespace-nowrap transition-all flex items-center justify-center gap-2 disabled:opacity-70 text-sm"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Subscribe
            </>
          )}
        </button>
      </form>
    );
  }

  // Default/Modal variant - stacked layout with card
  const content = (
    <div className={`bg-white rounded-2xl shadow-lg p-6 md:p-8 ${className}`}>
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-7 h-7 text-brand-secondary" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-brand-primary mb-2">
          Join My Newsletter
        </h3>
        <p className="text-slate-600">
          Get weekly insights on leadership, growth, and transformation.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="newsletter-name" className="block text-sm font-medium text-brand-primary mb-2">
            Name <span className="text-brand-secondary">*</span>
          </label>
          <input
            type="text"
            id="newsletter-name"
            name="name"
            required
            placeholder="Your name"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-brand-secondary focus:ring-2 focus:ring-brand-secondary/20 outline-none transition-all"
          />
        </div>
        <div>
          <label htmlFor="newsletter-email" className="block text-sm font-medium text-brand-primary mb-2">
            Email <span className="text-brand-secondary">*</span>
          </label>
          <input
            type="email"
            id="newsletter-email"
            name="email"
            required
            placeholder="your@email.com"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-brand-secondary focus:ring-2 focus:ring-brand-secondary/20 outline-none transition-all"
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 py-3 font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Subscribing...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Subscribe to Newsletter
            </>
          )}
        </button>
        <p className="text-slate-400 text-xs text-center">
          No spam, ever. Unsubscribe anytime.
        </p>
      </form>
    </div>
  );

  if (showAnimation) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {content}
      </motion.div>
    );
  }
  return content;
};

export default NewsletterForm;
